import {
  BaseResponseSchema,
  getRequiredMessage,
  MCustomNumberTypeNullish,
  MCustomNumberTypeRequired,
} from '@monetize/types/common';
import { toDate } from 'date-fns-tz';
import { formatISO } from 'date-fns/formatISO';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import { z } from 'zod';

// FIXME: This is here to avoid circular dependency
const replaceUserTimezoneWithUtc = (date: string): Date => {
  return toDate(date.substring(0, 19), { timeZone: 'Etc/UTC' });
};

export enum UsageTypeStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export const UsageTypeStatusEnumZ = z.enum(UsageTypeStatusEnum);
export interface IUsageType {
  id: string;
  name: string;
  pluralName: string;
  description: string;
  unitName: string;
  decimalPlaces: string | number;
  status: UsageTypeStatusEnum;
}

export const UsageTypeSchema = z.object({
  name: z
    .string()
    .max(40, { error: 'Name must be at most 40 characters long' })
    .nonempty(getRequiredMessage('Name')),
  pluralName: z
    .string()
    .max(40, {
      error: 'Plural Name must be at most 40 characters long',
    })
    .nonempty(getRequiredMessage('Plural Name')),
  description: z
    .string()
    .max(80, {
      error: 'Description must be at most 80 characters long',
    })
    .nullish(),
  unitName: z.string().max(40).nonempty(getRequiredMessage('Unit Name')),
  decimalPlaces: z
    .number({
      error: getRequiredMessage('Decimal Places'),
    })
    .min(0, {
      error: 'Decimal places must be 0 or greater',
    }),
  status: UsageTypeStatusEnumZ,
});

export type IUsageTypeSchema = z.infer<typeof UsageTypeSchema>;

export const UsageTypeResSchema = BaseResponseSchema.extend({
  name: z.string(),
  pluralName: z.string(),
  description: z.string().nullable(),
  unitName: z.string(),
  decimalPlaces: z.number(),
  status: UsageTypeStatusEnumZ,
});

export type IUsageTypeResSchema = z.infer<typeof UsageTypeResSchema>;

export enum UsageResponseStatusEnum {
  SUCCESS = 'SUCCESS',
  PARTIAL = 'PARTIAL',
  FAILED = 'FAILED',
}
export const UsageResponseStatusEnumZ = z.enum(UsageResponseStatusEnum);

export const UsageEvent = z.object({
  id: z.string(),
  accountId: z.string().min(1, { error: getRequiredMessage('Account ID') }),
  subscriptionId: z
    .string()
    .min(1, { error: getRequiredMessage('Subscription ID') }),
  usageTypeId: z
    .string()
    .min(1, { error: getRequiredMessage('Usage Type ID') }),
  unitsConsumed: MCustomNumberTypeRequired('Units Consumed'),
  timestamp: z.number(),
  ratedPrice: MCustomNumberTypeNullish,
  // Free-form key/value map. Set on create only — BE does not accept updates.
  properties: z.record(z.string(), z.string()).nullish(),
});
export type IUsageEvent = z.infer<typeof UsageEvent>;

// POST/PUT /usage/events
export const UsageEventRequestSchema = UsageEvent.extend({
  id: z.string().nonempty('Usage ID is required').max(200),
  timestamp: z.union([
    z
      .string()
      // User enters date in UTC, so we remove the running user's timezone offset
      .transform((val) => replaceUserTimezoneWithUtc(val))
      .refine((val) => isValid(val)),
    z.date().transform((val) => formatISO(val)),
  ]),
});
export type IUsageEventRequestSchema = z.infer<typeof UsageEventRequestSchema>;

export const AggregateUsageEventSchema = z.object({
  accountId: z.string(),
  subscriptionId: z.string(),
  unitsConsumed: z.number(),
  usageTypeId: z.string(),
});
export type AggregateUsageEvent = z.infer<typeof AggregateUsageEventSchema>;

// GET /usage/events
export const UsageEventListRequestSchema = z.object({
  subscriptionId: z.string().optional(),
  usageTypeIds: z.array(z.string()).optional(),
  startTime: z
    .string()
    .refine((val) => isValid(parseISO(val)))
    // Remove user timezone offset - date user chooses is in UTC
    .transform((val) =>
      val ? formatISO(replaceUserTimezoneWithUtc(val)) : '',
    ),
  endTime: z
    .string()
    .refine((val) => isValid(parseISO(val)))
    // Remove user timezone offset - date user chooses is in UTC
    .transform((val) =>
      val ? formatISO(replaceUserTimezoneWithUtc(val)) : '',
    ),
});
export type IUsageEventListRequestSchema = z.infer<
  typeof UsageEventListRequestSchema
>;

export const UsageAggregateEventListRequestSchema = z.object({
  accountIds: z.array(z.string()).optional(),
  subscriptionIds: z.array(z.string()).optional(),
  usageTypeIds: z.array(z.string()).optional(),
  startTime: z
    .string()
    .refine((val) => isValid(parseISO(val)))
    // Remove user timezone offset - date user chooses is in UTC
    .transform((val) =>
      val ? formatISO(replaceUserTimezoneWithUtc(val)) : '',
    ),
  endTime: z
    .string()
    .refine((val) => isValid(parseISO(val)))
    // Remove user timezone offset - date user chooses is in UTC
    .transform((val) =>
      val ? formatISO(replaceUserTimezoneWithUtc(val)) : '',
    ),
  pageSize: z.number().min(1).max(100).optional(),
  nextPageToken: z.string().optional(),
});
export type IUsageAggregateEventListRequestSchema = z.infer<
  typeof UsageAggregateEventListRequestSchema
>;

export const UsageEventResSchema = z.object({
  status: UsageResponseStatusEnumZ,
  failedEvents: z
    .object({
      event: UsageEventRequestSchema,
      reason: z.string(),
    })
    .array(),
});
export type IUsageEventResSchema = z.infer<typeof UsageEventResSchema>;

export const UsageEventUpdateReqSchema = UsageEvent.pick({
  unitsConsumed: true,
  ratedPrice: true,
});
export type IUsageEventUpdateReqSchema = z.infer<
  typeof UsageEventUpdateReqSchema
>;

export const UsageConsumptionResponseSchema = z.object({
  unitsConsumed: z.number(),
  ratedUnitsConsumed: z.number(),
  totalRatedPrice: z.number(),
});
export type UsageConsumptionResponse = z.infer<
  typeof UsageConsumptionResponseSchema
>;
